import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sanitizeInput } from '@/lib/sanitize';

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // Honeypot
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;

    // Honeypot check
    if (website) {
      console.log('Bot detected!');
      return; // Silently ignore
    }

    // Rate limiting check
    const lastSubmit = localStorage.getItem('last_newsletter_submit');
    const now = Date.now();
    if (lastSubmit && now - parseInt(lastSubmit) < 60000) { // 1 minute
      alert('Por favor, espera un minuto antes de intentar de nuevo.');
      return;
    }

    setSubmitting(true);
    
    // Sanitizar email
    const sanitizedEmail = sanitizeInput(email);
    
    try {
      // 1. Obtener IP del cliente
      let ip = 'Unknown';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        ip = ipData.ip;
      } catch (e) {
        console.error('Error fetching IP:', e);
      }

      // 2. Guardar en Firestore
      await addDoc(collection(db, 'newsletter_subscribers'), {
        email: sanitizedEmail,
        ip: ip,
        createdAt: serverTimestamp()
      });

      // 3. Enviar a la API externa (Readdy)
      const body = new URLSearchParams({ email: sanitizedEmail });
      await fetch('https://readdy.ai/api/form/d79jdcrll6jbq2vddohg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      localStorage.setItem('last_newsletter_submit', now.toString());
      setSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Error in newsletter subscription:', error);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0a0010] border-t border-white/10">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="https://static.readdy.ai/image/3aca7b4f25b4386bcee0afd7be9c4116/bf9b12c14ca0efd0a2bc13fcb2a88875.png"
              alt="Smart Business Digital Solutions"
              className="h-14 w-auto object-contain mb-4"
            />
            <p className="text-white/50 text-sm leading-relaxed mb-5">
              {t('common.footer.description')}
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-instagram-line', href: 'https://instagram.com' },
                { icon: 'ri-facebook-circle-line', href: 'https://facebook.com' },
                { icon: 'ri-tiktok-line', href: 'https://tiktok.com' },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-white/20 text-white/60 hover:text-white hover:border-[#8c52ff] hover:bg-[#8c52ff]/20 transition-all duration-300 cursor-pointer"
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-5">
              <a href="#links">{t('common.footer.navTitle')}</a>
            </h4>
            <ul className="space-y-3">
              {[
                { label: t('common.footer.links.home'), to: '/' },
                { label: t('common.footer.links.about'), to: '/about' },
                { label: t('common.footer.links.services'), to: '/services' },
                { label: t('common.footer.links.consulting'), to: '/business-consulting' },
                { label: t('common.footer.links.products'), to: '/products' },
                { label: t('common.footer.links.contact'), to: '/contact' },
                { label: t('common.footer.links.academy'), href: 'https://smartbusiness.academy' },
              ].map((link) => (
                <li key={link.label}>
                  {link.to ? (
                    <Link
                      to={link.to}
                      className="text-white/50 hover:text-[#00c2ff] text-sm transition-colors duration-300"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/50 hover:text-[#00c2ff] text-sm transition-colors duration-300"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-5">
              <a href="#services">{t('common.footer.servicesTitle')}</a>
            </h4>
            <ul className="space-y-3">
              {[
                'Creación de Sitio Web + Hosting',
                'Creación de CRM',
                'Branding & Diseño',
                'Consultoría en Redes Sociales',
                'Optimización de Redes Sociales',
                'Perfil de Google Business',
                'Automatización con IA & Funnels',
                'Consultoría & Estrategia',
                'Consultoría Empresarial',
              ].map((s) => (
                <li key={s}>
                  <Link to="/services" className="text-white/50 hover:text-[#00c2ff] text-sm transition-colors duration-300">
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-5">
              <a href="#newsletter">{t('common.footer.newsletterTitle')}</a>
            </h4>
            <p className="text-white/50 text-sm mb-4 leading-relaxed">
              {t('common.footer.newsletterDesc')}
            </p>
            {submitted ? (
              <div className="bg-[#8c52ff]/20 border border-[#8c52ff]/40 rounded-xl p-4 text-center">
                <i className="ri-check-line text-[#8c52ff] text-xl" />
                <p className="text-white/80 text-sm mt-1">{t('common.footer.subscribeSuccess')}</p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubscribe}
                className="flex flex-col gap-3"
              >
                {/* Honeypot */}
                <div className="hidden" aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                  className="bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#8c52ff]/60 transition-colors duration-300"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="whitespace-nowrap bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white text-sm font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity duration-300 cursor-pointer disabled:opacity-60"
                >
                  {submitting ? t('common.footer.subscribing') : t('common.footer.subscribe')}
                </button>
              </form>
            )}
            <div className="mt-5 space-y-2">
              <a href="mailto:info@sbdigisolutions.com" className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors duration-300">
                <i className="ri-mail-line text-[#8c52ff]" />
                info@sbdigisolutions.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/30 text-xs">
            {t('common.footer.rights')}
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-white/30 hover:text-white/60 text-xs transition-colors duration-300">{t('common.footer.privacy')}</a>
            <a href="#" className="text-white/30 hover:text-white/60 text-xs transition-colors duration-300">{t('common.footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
